import React from "react";

import pdf from "@react-pdf/renderer";

import { Features } from "@/config/features";
import { graphql } from "@/gql";
import { Maybe } from "@/gql/graphql";
import { calculateMacro } from "@/pages/Clients/ClientDetails/ClientDiary/Meals/utils/macros";
import { colors } from "@/theme/overrides/colors";
import { round } from "@/utils/number";
import { FragmentType } from "@/utils/type";

import Divider from "./Divider";
import Flex from "./layout/Flex";

const { Text, View, StyleSheet } = pdf;

const styles = StyleSheet.create({
  root: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "12px",
  },
  mealPlan: {
    fontWeight: "extrabold",
    fontSize: "20px",
  },
  cal: {
    fontWeight: "semibold",
    fontSize: "14px",
  },
  textSecondary: {
    color: colors.gray,
  },
  bold: {
    fontWeight: "bold",
  },
  caption: {
    fontSize: "10px",
  },
});

type Props = {
  // Kept so the day-header still fetches the plan's target split; the rendered
  // macros below now come from the actual planned foods (see totals props).
  data?: Maybe<FragmentType<typeof contentHeaderFragment>>;
  totalCalories: number;
  totalCarbohydrateGrams: number;
  totalProteinGrams: number;
  totalFatGrams: number;
};

const contentHeaderFragment = graphql(`
  fragment PDFMealPlanContentHeader on MealPlan {
    id
    macronutrientsCarbohydratePercent
    macronutrientsProteinPercent
    macronutrientsFatPercent
  }
`);

const ContentHeader: React.FC<Props> = ({
  totalCalories,
  totalCarbohydrateGrams,
  totalProteinGrams,
  totalFatGrams,
}) => {
  // Day-level macros are the sum of the actual planned foods, matching the
  // meal planner. Percentages are derived from those grams and the day's
  // total calories (same formula the planner uses).
  const carbohydrateGrams = totalCarbohydrateGrams;
  const proteinGrams = totalProteinGrams;
  const fatGrams = totalFatGrams;
  const macroPercent = calculateMacro(totalCalories);
  const carbohydratePercent = macroPercent.carbsPercentage(carbohydrateGrams);
  const proteinPercent = macroPercent.proteinPercentage(proteinGrams);
  const fatPercent = macroPercent.fatsPercentage(fatGrams);

  return (
    <View wrap={false}>
      <View style={styles.root}>
        <Flex alignItems="center">
          <Text style={styles.mealPlan}>Meal Plan</Text>
          <View style={{ marginLeft: "8px" }} />
          <Text style={styles.cal}>{`${Math.round(totalCalories)} kcal`}</Text>
        </Flex>

        {Features.pdfMacronutrientsEnabled && (
          <View style={{ width: "100%" }}>
            <Flex flexDirection="row" justifyContent="spaceBetween">
              <Flex flexDirection="row" alignItems="center">
                <Text style={[styles.bold, styles.caption]}>P:</Text>
                <View style={{ marginRight: "2px" }} />
                <Text
                  style={[styles.bold, styles.caption, styles.textSecondary]}
                >
                  {`${proteinPercent}%`}
                  {` (${round(proteinGrams, 2)}g)`}
                </Text>
              </Flex>
              <Flex
                flexDirection="row"
                alignItems="center"
                justifyContent="center"
              >
                <Text style={[styles.bold, styles.caption]}>F:</Text>
                <View style={{ marginRight: "2px" }} />
                <Text
                  style={[styles.bold, styles.caption, styles.textSecondary]}
                >
                  {`${fatPercent}%`}
                  {` (${round(fatGrams, 2)}g)`}
                </Text>
              </Flex>
              <Flex flexDirection="row" alignItems="center">
                <Text style={[styles.bold, styles.caption]}>C:</Text>
                <View style={{ marginRight: "2px" }} />
                <Text
                  style={[styles.bold, styles.caption, styles.textSecondary]}
                >
                  {`${carbohydratePercent}%`}
                  {` (${round(carbohydrateGrams, 2)}g)`}
                </Text>
              </Flex>
            </Flex>
          </View>
        )}
      </View>
      <View style={{ marginBottom: "8px" }} />
      <Divider size="large" />
    </View>
  );
};
export default ContentHeader;
